import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogOverlay, DialogPortal } from "./ui/dialog";
import {
  USER_TYPE_VISITOR,
  checkUserByMobile,
  getUserProfile,
  isApiSuccess,
  persistSession,
  readApiBody,
  readApiError,
  readRequestError,
  saveVisitor,
  sendSms,
  sendWhatsApp
} from "../services/authApi";

const WELCOME_MESSAGE = [
  "Welcome to venturesdirectory.com, India's first real estate network and advertisement platform, ",
  "revolutionizing the property landscape by connecting buyers, sellers, agents, builders, and real estate business partners. ",
  "We provide a dynamic hub where property aspirations meet unparalleled opportunities. Join us to collectively fulfill everyone's dream of owning a property",
  ".\n\n Experience BuyRentsale.com through this Product Demo Video Link : https://www.youtube.com/watch?v=adEo19mS2c4\n\nMore details and step-by-step guide on venturesdirectory.com ",
  "PDF Link : https://api.buyrentsale.com/uploads/defaults/buyrentsale.pdf \n\nTeam,\nBuyrentsale.com\nMobile:9618641100\nEmail: support@buyrentsale.com"
].join("");

// Must match the DLT-registered Buyrentsale.com SMS template ("Template not registered" if wording differs).
const otpSmsText = (otp) => `${otp} is your verification OTP for Buyrentsale.com. Please do not share this code with anyone. - AINEXT IT`;

const otpWhatsAppText = (otp) => `${otp} is your verification OTP for Buyrentsale.com. Please do not share this code with anyone. \n\nTeam,\nBuyrentsale.com\nMobile: 9618641100\nEmail: support@buyrentsale.com`;

const readUserId = (user) => user?.USERID || user?.UserID || 0;

function LoginModal({ open, onClose, onSuccess }) {
  const requestIdRef = useRef(0);
  const mobileInputRef = useRef(null);
  const otpInputRef = useRef(null);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpInfo, setOtpInfo] = useState("");
  const [userExists, setUserExists] = useState(false);
  const [userData, setUserData] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [otpError, setOtpError] = useState("");

  const reset = () => {
    requestIdRef.current += 1;
    setMobile("");
    setOtp("");
    setGeneratedOtp("");
    setOtpSent(false);
    setOtpInfo("");
    setUserExists(false);
    setUserData([]);
    setConfirmOpen(false);
    setLoading(false);
    setError("");
    setMobileError("");
    setOtpError("");
  };

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open]);

  useEffect(() => {
    if (otpSent) {
      otpInputRef.current?.focus();
    }
  }, [otpSent]);

  const closeModal = () => {
    reset();
    onClose();
  };

  const deliverOtp = async (mobileNumber, requestId) => {
    const nextOtp = String(Math.floor(100000 + Math.random() * 900000));
    setGeneratedOtp(nextOtp);
    setOtp("");
    setOtpError("");
    setOtpInfo("");
    setLoading(true);

    try {
      // SMS is the primary channel (DLT). WhatsApp is best-effort; do not treat WA-only HTTP 200 error bodies as success.
      await sendSms(otpSmsText(nextOtp), mobileNumber);
      sendWhatsApp(mobileNumber, otpWhatsAppText(nextOtp)).catch(() => {});

      if (requestId !== requestIdRef.current) {
        return;
      }

      setOtpSent(true);
      setOtpInfo(`An OTP has been sent to ${mobileNumber}. Please check and enter it.`);
    } catch {
      if (requestId === requestIdRef.current) {
        setGeneratedOtp("");
        setError("Unable to send OTP to this mobile number. Please try again later.");
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const finishLogin = async (users, requestId, isNewUser) => {
    const record = users?.[0];
    const userId = readUserId(record);
    if (!userId) {
      setError(isNewUser
        ? "USERID is missing in the response. Please try again."
        : "USERID is missing in existing data. Please try again.");
      return;
    }

    const authData = isNewUser
      ? {
        UserID: userId,
        UserName: userId,
        UserType: USER_TYPE_VISITOR,
        IsVisitor: record.IsVisitor || false,
        IsAgent: record.IsAgent || false,
        IsBuilder: record.IsBuilder || false
      }
      : {
        UserID: userId,
        UserName: userId,
        UserType: USER_TYPE_VISITOR,
        IsVisitor: record.IsVisitor || false,
        IsAgent: false,
        IsBuilder: false,
        IsProvider: false
      };

    const profileResponse = await getUserProfile(userId, USER_TYPE_VISITOR);
    if (requestId !== requestIdRef.current) {
      return;
    }

    const profile = readApiBody(profileResponse)?.[0];
    if (!isApiSuccess(profileResponse) || !profile) {
      setError("Unable to authenticate your details. Please try again later");
      return;
    }

    persistSession(authData, profile);
    onSuccess(profile);
  };

  const handleSendOtp = async (event) => {
    event.preventDefault();
    setError("");

    if (!/^\d{10}$/.test(mobile)) {
      setMobileError("Please enter a valid 10-digit number.");
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setUserExists(false);

    try {
      const response = await checkUserByMobile(mobile);
      if (requestId !== requestIdRef.current) {
        return;
      }

      if (!isApiSuccess(response)) {
        setError(readApiError(response, "Unable to validate the mobile number. Please try again."));
        setLoading(false);
        return;
      }

      const users = readApiBody(response);
      const list = Array.isArray(users) ? users : [];
      setUserData(list);

      if (list.length === 0) {
        setUserExists(false);
        setLoading(false);
        setConfirmOpen(true);
        return;
      }

      setUserExists(true);
      await deliverOtp(mobile, requestId);
    } catch (requestError) {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setError(readRequestError(requestError, "Something went wrong while validating the mobile number. Please try again later."));
      }
    }
  };

  const handleCreateAccount = async () => {
    setConfirmOpen(false);
    const requestId = ++requestIdRef.current;
    await deliverOtp(mobile, requestId);
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setError("");

    if (!/^\d{6}$/.test(otp)) {
      setOtpError("Please enter the OTP sent to your mobile.");
      return;
    }

    if (!generatedOtp || otp !== generatedOtp) {
      setError("Invalid OTP. Please try again.");
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      if (!userExists) {
        const response = await saveVisitor(mobile);
        if (requestId !== requestIdRef.current) {
          return;
        }

        const createdUsers = readApiBody(response);
        const created = Array.isArray(createdUsers) ? createdUsers[0] : null;
        if (!isApiSuccess(response) || !created || readUserId(created) === 0) {
          setError("There was an error while saving your details. Please try again later.");
          setLoading(false);
          return;
        }

        sendWhatsApp(mobile, WELCOME_MESSAGE).catch(() => {});
        await finishLogin(createdUsers, requestId, true);
      } else {
        await finishLogin(userData, requestId, false);
      }
    } catch (requestError) {
      if (requestId === requestIdRef.current) {
        setError(readRequestError(requestError, "Unable to authenticate your details. Please try again later"));
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  return <>
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen && !confirmOpen) closeModal(); }}>
      <DialogPortal>
        <DialogOverlay className="vd-login-overlay" style={{ zIndex: 1600 }} />
        <DialogPrimitive.Content
          className="vd-login-dialog"
          style={{ zIndex: 1610 }}
          aria-describedby="vd-login-description"
          onPointerDownOutside={(event) => {
            if (confirmOpen) event.preventDefault();
          }}
          onEscapeKeyDown={(event) => {
            if (confirmOpen) event.preventDefault();
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            mobileInputRef.current?.focus();
          }}
        >
          <div className="vd-login-header">
            <DialogPrimitive.Title className="vd-login-title">Verify with OTP</DialogPrimitive.Title>
            <button type="button" className="vd-login-x" onClick={closeModal} aria-label="Close">×</button>
          </div>

          <div className="vd-login-body">
            <DialogPrimitive.Description id="vd-login-description" className="vd-login-sr">
              Enter your WhatsApp mobile number to receive a one-time password.
            </DialogPrimitive.Description>

            {error ? <div className="vd-login-error" role="alert">{error}</div> : null}

            {!otpSent ? <form onSubmit={handleSendOtp} autoComplete="off">
              <div className="vd-login-field">
                <label htmlFor="vd-mobile-number">Enter Mobile No <span>(Whatsapp)</span></label>
                <input
                  ref={mobileInputRef}
                  id="vd-mobile-number"
                  className="form-control"
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit Mobile Number without +91"
                  value={mobile}
                  onChange={(event) => {
                    setMobile(event.target.value.replace(/\D/g, "").slice(0, 10));
                    setMobileError("");
                    setError("");
                  }}
                />
                {mobileError ? <div className="vd-login-field-error">{mobileError}</div> : null}
              </div>
              <button type="submit" className="btn btn-primary w-100" disabled={!/^\d{10}$/.test(mobile) || loading}>
                {loading ? "Please wait..." : "Send OTP"}
              </button>
            </form> : <form onSubmit={handleVerifyOtp} autoComplete="off">
              {otpInfo ? <div className="vd-login-otp-info" role="status">
                <span aria-hidden="true">✓</span>
                <p>{otpInfo}</p>
              </div> : null}
              <div className="vd-login-field">
                <label htmlFor="vd-otp-code">Enter OTP</label>
                <input
                  ref={otpInputRef}
                  id="vd-otp-code"
                  className="form-control"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(event) => {
                    setOtp(event.target.value.replace(/\D/g, "").slice(0, 6));
                    setOtpError("");
                    setError("");
                  }}
                />
                {otpError ? <div className="vd-login-field-error">{otpError}</div> : null}
              </div>
              <button type="submit" className="btn btn-primary w-100" disabled={!/^\d{6}$/.test(otp) || loading}>
                {loading ? "Please wait..." : "Verify OTP"}
              </button>
            </form>}
          </div>

          <div className="vd-login-footer">
            <button type="button" className="btn btn-primary" onClick={() => { closeModal(); window.location.assign("/"); }}>
              &lt;&lt; Home
            </button>
            <button type="button" className="btn btn-secondary" onClick={closeModal}>Close</button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>

    <Dialog open={confirmOpen} onOpenChange={(nextOpen) => { if (!nextOpen) setConfirmOpen(false); }}>
      <DialogPortal>
        <DialogOverlay className="vd-login-overlay" style={{ zIndex: 1800 }} />
        <DialogPrimitive.Content className="vd-login-confirm" style={{ zIndex: 1810 }} aria-describedby="vd-login-confirm-text">
          <DialogPrimitive.Title className="vd-login-sr">Create account</DialogPrimitive.Title>
          <p id="vd-login-confirm-text">The entered mobile number is not registered. Do you want to create new account ?</p>
          <div className="vd-login-confirm-actions">
            <button type="button" className="btn btn-danger" onClick={() => setConfirmOpen(false)}>No</button>
            <button type="button" className="btn btn-success" onClick={handleCreateAccount}>Yes</button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  </>;
}

export default LoginModal;
